import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	EllipsisIcon,
	MessageSquareIcon,
	PencilIcon,
	PinIcon,
	PinOffIcon,
	Trash2Icon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "./cn.js";
import type { MairkoThread } from "./mairko-threads.js";

type Props = {
	thread: MairkoThread;
	active: boolean;
	/** Agent / clone still working in this thread (even if you’re in another chat). */
	busy?: boolean;
	onSelect: () => void;
	onPin: () => void;
	onRename: (title: string) => void;
	onDelete: () => void;
};

/** Recent chat row with ChatGPT-style ⋯ actions (shadcn DropdownMenu). */
export function RecentThreadRow({
	thread,
	active,
	busy = false,
	onSelect,
	onPin,
	onRename,
	onDelete,
}: Props) {
	const [renaming, setRenaming] = useState(false);
	const [draft, setDraft] = useState(thread.title);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!renaming) return;
		setDraft(thread.title);
		const id = requestAnimationFrame(() => {
			inputRef.current?.focus();
			inputRef.current?.select();
		});
		return () => cancelAnimationFrame(id);
	}, [renaming, thread.title]);

	const commitRename = () => {
		const next = draft.trim();
		setRenaming(false);
		if (next && next !== thread.title) onRename(next);
	};

	if (renaming) {
		return (
			<li className="mc-recent-row">
				<form
					className="mc-recent mc-recent--renaming"
					onSubmit={(event) => {
						event.preventDefault();
						commitRename();
					}}
				>
					<MessageSquareIcon className="size-3.5 shrink-0 opacity-45" />
					<Input
						ref={inputRef}
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						onBlur={commitRename}
						onKeyDown={(event) => {
							if (event.key === "Escape") {
								event.preventDefault();
								setRenaming(false);
								setDraft(thread.title);
							}
						}}
						className="mc-recent-rename-input h-7 border-mx-sep bg-mx-elevated px-2 text-[13px] shadow-none"
						aria-label="Rename chat"
					/>
				</form>
			</li>
		);
	}

	return (
		<li className="mc-recent-row">
			<button
				type="button"
				className={cn(
					"mc-recent",
					active && "mc-recent--active",
					thread.pinned && "mc-recent--pinned",
					busy && "mc-recent--busy",
				)}
				onClick={onSelect}
				aria-busy={busy || undefined}
			>
				{busy ? (
					<span
						className="mc-recent-spinner"
						aria-hidden
						title="Working…"
					/>
				) : thread.pinned ? (
					<PinIcon className="size-3.5 shrink-0 opacity-55" />
				) : (
					<MessageSquareIcon className="size-3.5 shrink-0 opacity-45" />
				)}
				<span className="truncate">{thread.title}</span>
				{busy ? (
					<span className="sr-only">Working in this chat</span>
				) : null}
			</button>

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						className="mc-recent-menu-btn"
						aria-label={`Actions for ${thread.title}`}
						onClick={(event) => event.stopPropagation()}
					>
						<EllipsisIcon className="size-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-48" sideOffset={4}>
					<DropdownMenuItem
						onSelect={(event) => {
							event.preventDefault();
							onPin();
						}}
					>
						{thread.pinned ? (
							<PinOffIcon className="size-4" />
						) : (
							<PinIcon className="size-4" />
						)}
						{thread.pinned ? "Unpin" : "Pin"}
						<DropdownMenuShortcut>P</DropdownMenuShortcut>
					</DropdownMenuItem>
					<DropdownMenuItem
						onSelect={(event) => {
							event.preventDefault();
							setRenaming(true);
						}}
					>
						<PencilIcon className="size-4" />
						Rename
						<DropdownMenuShortcut>R</DropdownMenuShortcut>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem
						variant="destructive"
						onSelect={(event) => {
							event.preventDefault();
							onDelete();
						}}
					>
						<Trash2Icon className="size-4" />
						Delete
						<DropdownMenuShortcut>D</DropdownMenuShortcut>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</li>
	);
}
